/**
 * Auth-state helpers — single chokepoint for `localStorage` access so we
 * can flip the whole app from bearer-token-in-localStorage to httpOnly
 * cookies by changing one env var (`REACT_APP_AUTH_MODE=cookie`).
 *
 * Cookie mode assumes the backend:
 *   - Sets a Secure, httpOnly, SameSite=Lax (or Strict) session cookie
 *     on `/auth/login`.
 *   - Honours `withCredentials: true` and rotates the cookie on refresh.
 *   - Clears the cookie on `/auth/logout`.
 *
 * In cookie mode the frontend never sees the access token. We only keep
 * a non-sensitive `userId` in localStorage so the SPA can render the
 * "logged-in" chrome before the first API round-trip resolves.
 */
import { AUTH_MODE } from "./config";

const TOKEN_KEY = "accessToken";
const REFRESH_KEY = "refreshToken";
const USER_ID_KEY = "userId";

export const isCookieAuth = () => AUTH_MODE === "cookie";

export function isLoggedIn() {
  if (isCookieAuth()) {
    return Boolean(localStorage.getItem(USER_ID_KEY));
  }
  return Boolean(localStorage.getItem(TOKEN_KEY));
}

export function getUserId() {
  return localStorage.getItem(USER_ID_KEY);
}

export function getAccessToken() {
  if (isCookieAuth()) return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  if (isCookieAuth()) return null;
  return localStorage.getItem(REFRESH_KEY);
}

export function persistLogin({ accessToken, refreshToken, userId } = {}) {
  if (!isCookieAuth()) {
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  }
  if (userId !== undefined && userId !== null) {
    localStorage.setItem(USER_ID_KEY, String(userId));
  }
}

export function clearAuth() {
  // Wipe both legacy bearer keys + the userId hint so flipping modes
  // doesn't leave stale tokens behind.
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_ID_KEY);
}
